"use client";
import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { saveAs } from "file-saver";
import { useCostCodeMap } from "./useCostCodeMap";
import COPricingSectionsV3 from "./COPricingSectionsV3";
import CORevisionPanelV3 from "./CORevisionPanelV3";
import {
  GlobalItemsTable,
  EmailModal,
  generateCOPDF,
  countUnmapped,
} from "./COFormV3";

const API = "https://erp-production-72da01c8e651.herokuapp.com/api";
const cls = (...c) => c.filter(Boolean).join(" ");

const fmtC = (n) => {
  if (!Number.isFinite(n)) return "$0.00";
  const abs = Math.abs(n);
  const s = abs.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return n < 0 ? `-$${s}` : `$${s}`;
};

// ── Confirm dialog ─────────────────────────────────────────────────────────
function Confirm({
  open,
  title,
  message,
  label,
  color = "orange",
  onOk,
  onClose,
}) {
  if (!open) return null;
  const c = {
    orange: "bg-orange-500 hover:bg-orange-600",
    green: "bg-green-600 hover:bg-green-700",
    red: "bg-red-600   hover:bg-red-700",
  };
  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/60">
      <div className="bg-gray-800 border border-gray-600 rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
        <h3 className="text-base font-semibold text-orange-400 mb-2">
          {title}
        </h3>
        <p className="text-sm text-gray-300 mb-5 whitespace-pre-line">
          {message}
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-300 bg-gray-700 rounded-lg hover:bg-gray-600"
          >
            Cancel
          </button>
          <button
            onClick={onOk}
            className={cls(
              "px-4 py-2 text-sm text-white rounded-lg font-semibold",
              c[color],
            )}
          >
            {label}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Status badge ───────────────────────────────────────────────────────────
// Statuses: Pending | Approved | Rejected
// "Reverted" no longer exists — revert sets status back to Pending.
// revertedAt is shown as a small ↩ indicator on Pending COs so users
// can see at a glance this CO was previously approved and reverted.
export function StatusBadge({ status, revertedAt }) {
  const map = {
    Pending: "bg-yellow-800/60 text-yellow-200 border-yellow-700/40",
    Approved: "bg-green-800/60  text-green-200  border-green-700/40",
    Rejected: "bg-red-800/60    text-red-200    border-red-700/40",
  };
  const style = map[status] || "bg-gray-700 text-gray-400 border-gray-600";
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded border ${style}`}
    >
      {status}
      {status === "Pending" && revertedAt && (
        <span
          title={`Reverted ${new Date(revertedAt).toLocaleDateString()}`}
          className="text-[9px] text-yellow-400 opacity-70"
        >
          ↩
        </span>
      )}
    </span>
  );
}

// ── Summary tile ───────────────────────────────────────────────────────────
function Tile({ label, value, color = "text-white", str }) {
  return (
    <div className="bg-gray-900 rounded-lg border border-gray-700 px-4 py-3">
      <p className="text-[15px] text-white uppercase tracking-wide mb-1">
        {label}
      </p>
      <p className={cls("text-xl font-bold tabular-nums", color)}>
        {str ? value : fmtC(value)}
      </p>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
export default function CODetailV3({
  co,
  purchaseOrders,
  workOrders,
  databaseItems,
  companyProfile,
  projectData,
  token,
  onBack,
  onUpdated,
  onEdit,
  toast,
  contractLabourRates = [], // ← ADD
}) {
  const headers = { Authorization: `Bearer ${token}` };
  const [linkedTandMScopes, setLinkedTandMScopes] = useState([]);
  const [tandmPdfBlobs, setTandmPdfBlobs] = useState([]);
  const [confirm, setConfirm] = useState(null);
  const [acting, setActing] = useState(false);
  const [viewRev, setViewRev] = useState(null);
  const [genPDF, setGenPDF] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [contacts, setContacts] = useState([]);
  // In CODetailV3, add state near the top alongside other useState calls
  const [itemsExpanded, setItemsExpanded] = useState(false);
  const [localSections, setLocalSections] = useState(() =>
    (co.pricingSections || []).map((s) => {
      const seen = new Set();
      return {
        ...s,
        customItems: (s.customItems || []).map((ci, idx) => {
          let id =
            (ci._id && typeof ci._id === "object"
              ? ci._id.toString()
              : ci._id) ||
            `ci-load-${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 7)}`;
          while (seen.has(id)) {
            id = `${id}-dup-${Math.random().toString(36).slice(2, 7)}`;
          }
          seen.add(id);
          return { ...ci, _id: id };
        }),
      };
    }),
  );
  const [emailForm, setEmailForm] = useState({
    to: "",
    subject: `Change Order CO3-${co.changeOrderNumber}${projectData?.projectName ? ` — ${projectData.projectName}` : ""}`,
    body: "Dear Client,\n\nPlease find attached the Change Order for your review.\n\nThank you,",
  });

  // Cost codes for section name resolution
  const { codeMap } = useCostCodeMap();
  const costCodes = useMemo(
    () =>
      [...codeMap.entries()].map(([code, name]) => ({
        code,
        name,
        active: true,
      })),
    [codeMap],
  );

  useEffect(() => {
    if (!co?._id || !projectData?._id) return;
    const coId = co._id.toString();
    axios
      .get(`${API}/tandm3/project/${projectData._id}`, { headers })
      .then((r) => {
        const scopes = [];
        for (const tandm of r.data.tandms || []) {
          for (const scope of tandm.scopes || []) {
            const lid =
              scope.linkedChangeOrderId?._id?.toString() ||
              scope.linkedChangeOrderId?.toString?.() ||
              null;
            if (lid === coId && scope.status === "Approved") {
              scopes.push({ tandm, scope });
            }
          }
        }
        setLinkedTandMScopes(scopes);
      })
      .catch(() => {});
  }, [co._id, projectData?._id]);

  // ── Derived status flags ───────────────────────────────────────────────
  // "Reverted" is no longer a status — revert sets Pending.
  // All three flags are mutually exclusive.
  const isApproved = co.status === "Approved";
  const isPending = co.status === "Pending";
  const isRejected = co.status === "Rejected";

  const unmapped = useMemo(() => countUnmapped(co.items || []), [co.items]);
  const missingSections = useMemo(
    () => (co.items || []).filter((i) => !i.sectionNumber?.trim()).length,
    [co.items],
  );

  useEffect(() => {
    setLocalSections(
      (co.pricingSections || []).map((s) => {
        const seen = new Set();
        return {
          ...s,
          customItems: (s.customItems || []).map((ci, idx) => {
            let id =
              (ci._id && typeof ci._id === "object"
                ? ci._id.toString()
                : ci._id) ||
              `ci-load-${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 7)}`;
            while (seen.has(id)) {
              id = `${id}-dup-${Math.random().toString(36).slice(2, 7)}`;
            }
            seen.add(id);
            return { ...ci, _id: id };
          }),
        };
      }),
    );
  }, [co._id]);

  const items = useMemo(
    () =>
      (co.items || []).map((item, i) => ({
        ...item,
        _localId: item._id?.toString() || `di-${i}`,
      })),
    [co.items],
  );

  useEffect(() => {
    setLocalSections(co.pricingSections || []);
  }, [co._id]);

  // ── Financials ─────────────────────────────────────────────────────────
  const totalCost = (co.items || []).reduce((s, i) => s + (i.amount || 0), 0);
  const dispSections = viewRev
    ? viewRev.pricingSections || []
    : co.pricingSections || [];
  const customerSub = dispSections.reduce(
    (s, sec) => s + (sec.materialPrice || 0) + (sec.laborPrice || 0),
    0,
  );
  const ohRate = co.overheadPercentage || 0;
  const overhead = customerSub * (ohRate / 100);
  const customerTotal = customerSub + overhead;
  const margin =
    customerTotal > 0
      ? ((customerTotal - Math.abs(totalCost)) / customerTotal) * 100
      : 0;

  const knownBidAreas = useMemo(() => {
    const areas = new Set();
    (purchaseOrders || []).forEach((p) => {
      if (p.bidArea) areas.add(p.bidArea);
    });
    (workOrders || []).forEach((w) => {
      if (w.bidArea) areas.add(w.bidArea);
    });
    items.forEach((i) => {
      if (i.bidArea) areas.add(i.bidArea);
    });
    return Array.from(areas).sort();
  }, [purchaseOrders, workOrders, items]);

  useEffect(() => {
    const acctId = projectData?.accountId?._id || projectData?.accountId;
    if (!acctId) return;
    axios
      .get(`${API}/contacts/related?relatedAccount=${acctId}`, { headers })
      .then((r) => setContacts(r.data || []))
      .catch(() => {});
  }, [projectData]);

  // ── Load linked T&M scopes ─────────────────────────────────────────────────
  useEffect(() => {
    if (!co?._id || !projectData?._id) return;
    const coId = co._id.toString();
    axios
      .get(`${API}/tandm3/project/${projectData._id}`, { headers })
      .then((r) => {
        const scopes = [];
        for (const tandm of r.data.tandms || []) {
          for (const scope of tandm.scopes || []) {
            const lid =
              scope.linkedChangeOrderId?._id?.toString() ||
              (typeof scope.linkedChangeOrderId === "string"
                ? scope.linkedChangeOrderId
                : null);
            if (lid === coId && scope.status === "Approved") {
              scopes.push({ tandm, scope });
            }
          }
        }
        setLinkedTandMScopes(scopes);
      })
      .catch(() => {});
  }, [co._id, projectData?._id]);

  // ── Merge helper ───────────────────────────────────────────────────────────
  const mergeAllPDFs = async (blobs) => {
    const { PDFDocument } = await import("pdf-lib");
    const merged = await PDFDocument.create();
    for (const blob of blobs) {
      try {
        const arrayBuffer = await blob.arrayBuffer();
        const pdf = await PDFDocument.load(arrayBuffer);
        const pages = await merged.copyPages(pdf, pdf.getPageIndices());
        pages.forEach((page) => merged.addPage(page));
      } catch (err) {
        console.warn("Failed to merge a PDF page:", err.message);
      }
    }
    const mergedBytes = await merged.save();
    return new Blob([mergedBytes], { type: "application/pdf" });
  };

  // ── Fetch T&M PDFs — self-contained, does NOT rely on linkedTandMScopes state
  // fetches fresh from the API each time so it works even if state isn't ready
  const fetchLinkedTandMPdfs = async () => {
    if (!co?._id || !projectData?._id) return [];
    const coId = co._id.toString();

    let allTandMs = [];
    try {
      const res = await axios.get(`${API}/tandm3/project/${projectData._id}`, {
        headers,
      });
      allTandMs = res.data.tandms || [];
    } catch (err) {
      console.warn("Could not fetch T&Ms for PDF:", err.message);
      return [];
    }

    const { generateScopePDF } = await import("./TimeAndMaterialTab3");
    const blobs = [];

    for (const tandm of allTandMs) {
      for (const scope of tandm.scopes || []) {
        const lid =
          scope.linkedChangeOrderId?._id?.toString() ||
          (typeof scope.linkedChangeOrderId === "string"
            ? scope.linkedChangeOrderId
            : null);
        if (lid !== coId || scope.status !== "Approved") continue;
        try {
          const result = await generateScopePDF(
            tandm,
            scope,
            projectData,
            companyProfile,
          );
          blobs.push(result.blob);
        } catch (err) {
          console.warn(`Scope ${scope._id} PDF failed:`, err.message);
        }
      }
    }
    return blobs;
  };

  // ── PDF download ───────────────────────────────────────────────────────────
  const downloadPDF = async () => {
    setGenPDF(true);
    try {
      const coBlob = await generateCOPDF({
        co,
        sections: co.pricingSections || [],
        items,
        companyProfile,
        projectData,
        markupPct: co.defaultMarkupPercentage,
        overheadPct: co.overheadPercentage,
        headers,
      });

      const tandmBlobs = await fetchLinkedTandMPdfs();
      const allBlobs = [coBlob, ...tandmBlobs];
      const finalBlob =
        allBlobs.length > 1 ? await mergeAllPDFs(allBlobs) : coBlob;

      saveAs(
        finalBlob,
        `ChangeOrder_${co.changeOrderNumber}${tandmBlobs.length > 0 ? "_with_TandM" : ""}.pdf`,
      );

      toast(
        "success",
        tandmBlobs.length > 0
          ? `Combined PDF downloaded (CO + ${tandmBlobs.length} T&M scope${tandmBlobs.length !== 1 ? "s" : ""})`
          : "PDF downloaded",
      );
    } catch (err) {
      toast("error", `PDF error: ${err.message}`);
    } finally {
      setGenPDF(false);
    }
  };

  // ── Prep email ─────────────────────────────────────────────────────────────
  const prepEmail = async () => {
    setGenPDF(true);
    try {
      const coBlob = await generateCOPDF({
        co,
        sections: co.pricingSections || [],
        items,
        companyProfile,
        projectData,
        markupPct: co.defaultMarkupPercentage,
        overheadPct: co.overheadPercentage,
        headers,
      });

      const tandmBlobs = await fetchLinkedTandMPdfs();
      const allBlobs = [coBlob, ...tandmBlobs];
      const finalBlob =
        allBlobs.length > 1 ? await mergeAllPDFs(allBlobs) : coBlob;

      setPdfBlob(finalBlob);
      setTandmPdfBlobs(tandmBlobs);
      setShowEmail(true);
    } catch (err) {
      toast("error", `PDF error: ${err.message}`);
    } finally {
      setGenPDF(false);
    }
  };

  // ── Send email ─────────────────────────────────────────────────────────────
  const sendEmail = async () => {
    if (!emailForm.to || !pdfBlob) return;
    setSendingEmail(true);
    try {
      const fd = new FormData();
      fd.append("to", emailForm.to);
      fd.append("subject", emailForm.subject);
      fd.append("body", emailForm.body || "");

      const filename =
        tandmPdfBlobs.length > 0
          ? `ChangeOrder_${co.changeOrderNumber}_with_TandM.pdf`
          : `ChangeOrder_${co.changeOrderNumber}.pdf`;

      fd.append("files", pdfBlob, filename);

      await axios.post(`${API}/changeOrders/${co._id}/send-email`, fd, {
        headers,
      });

      setShowEmail(false);
      setPdfBlob(null);
      setTandmPdfBlobs([]);
      toast("success", "Email sent");
    } catch (err) {
      toast("error", err.response?.data?.message || err.message);
    } finally {
      setSendingEmail(false);
    }
  };

  const buildCombinedPDF = async (coBlob, tandmBlobs) => {
    if (!tandmBlobs.length) return coBlob;
    const { mergePDFBlobs } = await import("./TimeAndMaterialTab3");
    return mergePDFBlobs([coBlob, ...tandmBlobs.map((t) => t.blob)]);
  };

  // ── Action handler ─────────────────────────────────────────────────────
  // Handles approve / reject / revert uniformly.
  // After revert the backend returns status:"Pending" — onUpdated propagates
  // this so the UI re-renders with the correct isPending flag.
  const doAction = async (type) => {
    setActing(true);
    try {
      const res = await axios.post(
        `${API}/changeOrders3/${co._id}/${type}`,
        {},
        { headers },
      );
      const updated = res.data.changeOrder;

      const successMessages = {
        approve: "Change order approved",
        reject: "Change order rejected",
        revert: "Change order reverted to Pending",
      };
      toast("success", successMessages[type] || `Change order ${type}d`);
      onUpdated(updated);
    } catch (err) {
      toast("error", err.response?.data?.message || err.message);
    } finally {
      setActing(false);
      setConfirm(null);
    }
  };

  // ── Confirm dialog configs ─────────────────────────────────────────────
  const CONFIRM = {
    approve: {
      type: "approve",
      title: "Approve Change Order",
      message:
        "Push M item deltas to mapped POs and L item deltas to mapped WOs.",
      label: "Approve",
      color: "green",
    },
    reapprove: {
      type: "approve",
      title: "Re-approve Change Order",
      message:
        "Push deltas to mapped POs / WOs.\n\n" +
        "Any existing actuals (material orders, piecework logs) placed against " +
        "this CO's items will automatically re-attach to the new delta rows.",
      label: "Approve",
      color: "green",
    },
    reject: {
      type: "reject",
      title: "Reject Change Order",
      message:
        "No deltas will be pushed. The CO can be edited and re-approved later.",
      label: "Reject",
      color: "red",
    },
    revert: {
      type: "revert",
      title: "Revert to Pending",
      message:
        "This removes the CO's budget deltas from all mapped POs and WOs and " +
        "returns the CO to Pending status.\n\n" +
        "Any material orders or piecework logs placed against this CO's items " +
        "will remain — their actuals re-attach automatically when the CO is " +
        "re-approved.\n\n" +
        "You can then edit, re-approve, or reject the CO.",
      label: "Revert to Pending",
      color: "orange",
    },
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-5">
      {/* Confirm dialog */}
      <Confirm
        open={!!confirm}
        title={confirm?.title}
        message={confirm?.message}
        label={confirm?.label}
        color={confirm?.color}
        onOk={() => doAction(confirm?.type)}
        onClose={() => setConfirm(null)}
      />

      {/* Email modal */}
      <EmailModal
        open={showEmail}
        onClose={() => {
          setShowEmail(false);
          setPdfBlob(null);
        }}
        form={emailForm}
        setForm={setEmailForm}
        onSend={sendEmail}
        isSending={sendingEmail}
        contacts={contacts}
      />

      {/* Revision snapshot banner */}
      {viewRev && (
        <div className="bg-gray-700/80 border border-indigo-500/60 rounded-lg px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-white text-sm font-semibold">
              👁 {viewRev.label || `Revision ${viewRev.revisionNumber}`}
            </span>
            {viewRev.createdAt && (
              <span className="text-gray-400 text-xs">
                {new Intl.DateTimeFormat("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                }).format(new Date(viewRev.createdAt))}
              </span>
            )}
            <span className="text-[10px] bg-orange-600 text-white px-2 py-0.5 rounded">
              Read-only snapshot
            </span>
          </div>
          <button
            onClick={() => setViewRev(null)}
            className="px-3 py-1.5 text-xs text-white bg-gray-600 rounded hover:bg-gray-500"
          >
            × Live
          </button>
        </div>
      )}

      {/* ── Header card ─────────────────────────────────────────────────── */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          {/* Left: identity */}
          <div>
            <div className="flex items-center gap-3 mb-1 flex-wrap">
              <button
                onClick={onBack}
                className="text-gray-400 hover:text-white text-sm transition-colors"
              >
                ← Back
              </button>

              <span className="text-gray-600">|</span>
              <span className="text-[11px] font-mono text-gray-500">
                CO3-{co.changeOrderNumber}
              </span>
              <StatusBadge status={co.status} revertedAt={co.revertedAt} />

              {co.billingMode === "separate" && (
                <span className="px-2 py-0.5 text-[11px] bg-purple-900/40 text-purple-300 rounded-full border border-purple-700/40">
                  Separate Invoice
                </span>
              )}
              {co.billingMode === "nonBillable" && (
                <span className="px-2 py-0.5 text-[11px] bg-gray-700 text-gray-400 rounded-full border border-gray-600">
                  Non-Billable
                  {co.nonBillableReason ? ` — ${co.nonBillableReason}` : ""}
                </span>
              )}
            </div>
            <h2 className="text-xl font-bold text-white">{co.title}</h2>
            {co.referenceNumber && (
              <p className="text-xs text-gray-500 mt-0.5">
                Ref: {co.referenceNumber}
              </p>
            )}
            {co.customerDescription && (
              <p className="text-sm text-gray-300 mt-2">
                {co.customerDescription}
              </p>
            )}
          </div>

          {/* Right: action buttons — only shown when not viewing a revision */}
          {!viewRev && (
            <div className="flex flex-wrap gap-2 items-start">
              {/* Always available */}
              <button
                onClick={downloadPDF}
                disabled={genPDF}
                className="px-3 py-1.5 text-xs text-blue-300 bg-blue-900/30 border border-blue-700/40 rounded hover:bg-blue-900/50 disabled:opacity-50 transition-colors"
              >
                {genPDF ? "…" : "↓ PDF"}
              </button>
              <button
                onClick={prepEmail}
                disabled={genPDF}
                className="px-3 py-1.5 text-xs text-green-300 bg-green-900/30 border border-green-700/40 rounded hover:bg-green-900/50 disabled:opacity-50 transition-colors"
              >
                ✉ Email
              </button>

              {/* ── PENDING actions ───────────────────────────────────── */}
              {isPending && (
                <>
                  <button
                    onClick={onEdit}
                    className="px-3 py-1.5 text-xs font-semibold text-orange-300 bg-orange-900/30 border border-orange-700/40 rounded hover:bg-orange-900/50 transition-colors"
                  >
                    ✎ Edit
                  </button>

                  {/* Validation warnings — shown instead of Approve when blocked */}
                  {unmapped > 0 && (
                    <div className="px-3 py-1.5 text-xs text-yellow-300 bg-yellow-900/20 border border-yellow-700/30 rounded">
                      ⚠ {unmapped} unmapped (M→PO, L→WO)
                    </div>
                  )}
                  {missingSections > 0 && (
                    <div className="px-3 py-1.5 text-xs text-red-300 bg-red-900/20 border border-red-700/30 rounded">
                      ⛔ {missingSections} missing section #
                    </div>
                  )}

                  {unmapped === 0 && missingSections === 0 && (
                    <button
                      disabled={acting}
                      onClick={() =>
                        setConfirm(
                          co.revertedAt ? CONFIRM.reapprove : CONFIRM.approve,
                        )
                      }
                      className="px-3 py-1.5 text-xs font-semibold text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-40 transition-colors"
                    >
                      ✓ {co.revertedAt ? "Re-approve" : "Approve"}
                    </button>
                  )}

                  <button
                    disabled={acting}
                    onClick={() => setConfirm(CONFIRM.reject)}
                    className="px-3 py-1.5 text-xs font-semibold text-white bg-red-600 rounded hover:bg-red-700 disabled:opacity-40 transition-colors"
                  >
                    ✕ Reject
                  </button>
                </>
              )}

              {/* ── APPROVED actions ──────────────────────────────────── */}
              {isApproved && (
                <button
                  disabled={acting}
                  onClick={() => setConfirm(CONFIRM.revert)}
                  className="px-3 py-1.5 text-xs font-semibold text-white bg-gray-600 rounded hover:bg-gray-500 disabled:opacity-40 transition-colors"
                >
                  ↩ Revert to Pending
                </button>
              )}

              {/* ── REJECTED actions ──────────────────────────────────── */}
              {isRejected && (
                <>
                  <button
                    onClick={onEdit}
                    className="px-3 py-1.5 text-xs font-semibold text-orange-300 bg-orange-900/30 border border-orange-700/40 rounded hover:bg-orange-900/50 transition-colors"
                  >
                    ✎ Edit
                  </button>

                  {unmapped === 0 && missingSections === 0 && (
                    <button
                      disabled={acting}
                      onClick={() => setConfirm(CONFIRM.reapprove)}
                      className="px-3 py-1.5 text-xs font-semibold text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-40 transition-colors"
                    >
                      ✓ Approve
                    </button>
                  )}
                  {(unmapped > 0 || missingSections > 0) && (
                    <div className="px-3 py-1.5 text-xs text-yellow-300 bg-yellow-900/20 border border-yellow-700/30 rounded">
                      ⚠ Fix{" "}
                      {unmapped > 0
                        ? `${unmapped} unmapped`
                        : `${missingSections} missing section`}{" "}
                      before approving
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Revision panel */}
        {!viewRev && (
          <div className="mt-4">
            <CORevisionPanelV3
              changeOrderId={co._id}
              token={token}
              isApproved={isApproved}
              onViewRevision={setViewRev}
              showAlert={(type, title, msg) => toast(type, msg || title)}
            />
          </div>
        )}
      </div>

      {/* ── Summary tiles ───────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <Tile
          label="Budget Cost"
          value={totalCost}
          color={totalCost < 0 ? "text-red-400" : "text-green-400"}
        />
        <Tile
          label="Customer Sub"
          value={customerSub}
          color={customerSub < 0 ? "text-red-400" : "text-gray-200"}
        />
        <Tile
          label={`Overhead (${ohRate}%)`}
          value={overhead}
          color={overhead < 0 ? "text-red-400" : "text-gray-300"}
        />
        <Tile
          label="Customer Total"
          value={customerTotal}
          color={customerTotal < 0 ? "text-red-400" : "text-orange-400"}
        />
        <Tile
          label="Margin"
          value={`${margin.toFixed(1)}%`}
          color={margin >= 0 ? "text-green-400" : "text-red-400"}
          str
        />
      </div>

      {linkedTandMScopes.length > 0 && !viewRev && (
        <div className="bg-gray-800 rounded-xl border border-purple-700/40 overflow-hidden">
          <div className="flex items-center gap-3 px-5 py-3 bg-purple-900/20 border-b border-purple-700/30">
            <span className="text-xs font-bold text-purple-300 uppercase tracking-wider">
              📋 Linked T&M Scopes
            </span>
            <span className="text-[10px] px-2 py-0.5 bg-purple-900/50 text-purple-300 border border-purple-700/50 rounded-full font-bold">
              {linkedTandMScopes.length} scope
              {linkedTandMScopes.length !== 1 ? "s" : ""}
            </span>
            <span className="text-[10px] text-purple-500">
              — PDFs included when downloading or emailing
            </span>
          </div>
          <div className="divide-y divide-gray-700/40">
            {linkedTandMScopes.map(({ tandm, scope }) => {
              const mCount = (scope.items || []).filter(
                (i) => i.category === "M",
              ).length;
              const lCount = (scope.items || []).filter(
                (i) => i.category === "L",
              ).length;
              return (
                <div
                  key={scope._id}
                  className="flex items-center gap-3 px-5 py-2.5"
                >
                  <span className="text-[10px] font-mono font-bold text-orange-400 shrink-0">
                    T&M #{tandm.timeAndMaterialNumber}
                  </span>
                  {scope.scopeNumber != null && (
                    <span className="text-[10px] font-mono text-gray-500 shrink-0">
                      Entry #{String(scope.scopeNumber).padStart(2, "0")}
                    </span>
                  )}
                  <span className="text-xs text-gray-300 font-medium truncate flex-1">
                    {scope.name}
                  </span>
                  {scope.note && (
                    <span className="text-[10px] text-gray-500 truncate max-w-[140px] shrink-0">
                      {scope.note}
                    </span>
                  )}
                  <div className="flex items-center gap-1.5 shrink-0">
                    {mCount > 0 && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-blue-900/50 text-blue-300 rounded">
                        {mCount}M
                      </span>
                    )}
                    {lCount > 0 && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-yellow-900/50 text-yellow-300 rounded">
                        {lCount}L
                      </span>
                    )}
                    {scope.signedBy && (
                      <span className="text-[10px] text-green-400">
                        ✓ {scope.signedBy}
                      </span>
                    )}
                    {scope.approvedAt && (
                      <span className="text-[10px] text-gray-500">
                        {new Date(scope.approvedAt).toLocaleDateString()}
                      </span>
                    )}
                    <span
                      className={cls(
                        "text-[10px] px-1.5 py-0.5 rounded border",
                        scope.convertedToChangeOrder
                          ? "bg-gray-700 text-gray-500 border-gray-600"
                          : "bg-purple-900/40 text-purple-400 border-purple-700/40",
                      )}
                    >
                      {scope.convertedToChangeOrder ? "converted" : "linked"}
                    </span>
                    <button
                      onClick={async () => {
                        if (
                          !window.confirm(
                            `Unlink "${scope.name}" from this CO? It will no longer be included in PDF exports and can be linked to a different CO.`,
                          )
                        )
                          return;
                        try {
                          await axios.patch(
                            `${API}/tandm3/${tandm._id}/scopes/${scope._id}/unlink-co`,
                            {},
                            { headers },
                          );
                          setLinkedTandMScopes((p) =>
                            p.filter((s) => s.scope._id !== scope._id),
                          );
                        } catch (err) {
                          toast(
                            "error",
                            err.response?.data?.message ||
                              "Failed to unlink scope",
                          );
                        }
                      }}
                      className="text-[10px] px-2 py-0.5 rounded border border-red-800/40 text-red-400 bg-red-900/20 hover:bg-red-900/40 hover:border-red-700 transition-colors"
                      title="Unlink this scope from the CO"
                    >
                      Unlink
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Cost items (read-only) ───────────────────────────────────────── */}
      {!viewRev && (
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-white">
              Cost Items ({(co.items || []).length})
            </h3>
            <button
              onClick={() => setItemsExpanded((p) => !p)}
              className="px-3 py-1.5 text-xs text-white bg-orange-700 border border-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              {itemsExpanded
                ? "▲ Collapse"
                : `▼ Show ${(co.items || []).length} item${(co.items || []).length !== 1 ? "s" : ""}`}
            </button>
          </div>
          {itemsExpanded && (
            <GlobalItemsTable
              items={items}
              setItems={() => {}}
              purchaseOrders={purchaseOrders}
              workOrders={workOrders}
              isReadOnly={true}
              knownBidAreas={knownBidAreas}
              costCodes={costCodes}
            />
          )}
        </div>
      )}

      {/* ── Pricing sections ────────────────────────────────────────────── */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-semibold text-white">
              Customer Pricing
            </h3>
            {(isPending || isRejected) && (
              <p className="text-xs text-gray-500 mt-0.5">
                Click{" "}
                <span className="text-orange-400 font-medium">✎ Edit</span>{" "}
                above to modify sections, items, and pricing.
              </p>
            )}
            {isApproved && (
              <p className="text-xs text-gray-500 mt-0.5">
                Approved —{" "}
                <span className="text-orange-400 font-medium">✎ Edit</span> is
                disabled. Revert to Pending first to make changes.
              </p>
            )}
          </div>
        </div>
        <COPricingSectionsV3
          sections={viewRev ? viewRev.pricingSections || [] : localSections}
          setSections={viewRev ? () => {} : setLocalSections}
          items={
            viewRev
              ? (viewRev.items || []).map((item, i) => ({
                  ...item,
                  _localId: item._id?.toString() || `rv-${i}`,
                }))
              : items
          }
          contractLabourRates={contractLabourRates}
          setItems={() => {}}
          markupPct={co.defaultMarkupPercentage}
          overheadPct={
            viewRev
              ? viewRev.overheadPercentage || 0
              : co.overheadPercentage || 0
          }
          isReadOnly={true}
          databaseItems={databaseItems}
          purchaseOrders={purchaseOrders}
          workOrders={workOrders}
          knownBidAreas={knownBidAreas}
          costCodes={costCodes}
        />
      </div>
    </div>
  );
}
